import { useEffect, useState } from "react";

/**
 * Returns a copy of `value` that only updates after `delayMs` has elapsed
 * without `value` changing. Rapid updates within the delay window are
 * collapsed into a single trailing update.
 *
 * `delayMs` is a required parameter (not a module constant) so callers can
 * pass `0` in tests/stories to make debounced behavior deterministic without
 * waiting on real timers.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delayMs);

    return () => clearTimeout(timeoutId);
  }, [value, delayMs]);

  return debouncedValue;
}
